import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { 
  CreditCard, 
  Calendar, 
  IndianRupee, 
  CheckCircle2,
  Clock,
  History,
  Wallet,
  Building2,
  FileStack
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const BANK_ACCOUNTS = ["592", "OD", "Z-Del", "Z-Dine", "S-Del", "S-Dine", "Easy-Din", "G-Rabit", "Loan"];

const PayBills = ({ user }) => {
  const [activeTab, setActiveTab] = useState('today');
  const [todayBills, setTodayBills] = useState([]);
  const [currentMonthBills, setCurrentMonthBills] = useState([]);
  const [oldMonthBills, setOldMonthBills] = useState([]);
  const [openingLiabilities, setOpeningLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Vendor filter
  const [vendorFilter, setVendorFilter] = useState('all');
  const [vendors, setVendors] = useState([]);
  
  // Selected bills for payment
  const [selectedBills, setSelectedBills] = useState({});
  const [paymentAmounts, setPaymentAmounts] = useState({});
  
  // Payment details
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [bankAccount, setBankAccount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [remarks, setRemarks] = useState('');
  
  const [processing, setProcessing] = useState(false);

  const canPay = ['accountant', 'admin', 'general_manager'].includes(user?.role);

  useEffect(() => {
    fetchBills();
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  const fetchBills = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [todayRes, currentRes, oldRes, liabilitiesRes] = await Promise.all([
        axios.get(`${API}/purchase-bills/payable/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-bills/payable/current-month`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-bills/payable/old-months`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/opening-liabilities/payable/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTodayBills(todayRes.data);
      setCurrentMonthBills(currentRes.data);
      setOldMonthBills(oldRes.data);
      setOpeningLiabilities(liabilitiesRes.data || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch bills');
      setLoading(false);
    }
  };

  const getCurrentBills = () => {
    let bills;
    switch (activeTab) {
      case 'today': bills = todayBills; break;
      case 'current_month': bills = currentMonthBills; break;
      case 'old_month': bills = oldMonthBills; break;
      case 'opening_liabilities': bills = openingLiabilities; break;
      default: bills = [];
    }
    
    // Apply vendor filter (for regular bills, not opening liabilities)
    if (vendorFilter !== 'all' && activeTab !== 'opening_liabilities') {
      bills = bills.filter(b => b.vendor_id === vendorFilter);
    }
    
    return bills;
  };

  // Get unique vendors from current bills for filter dropdown
  const getVendorsForCurrentTab = () => {
    let bills;
    switch (activeTab) {
      case 'today': bills = todayBills; break;
      case 'current_month': bills = currentMonthBills; break;
      case 'old_month': bills = oldMonthBills; break;
      default: return [];
    }
    
    const vendorMap = new Map();
    bills.forEach(b => {
      if (b.vendor_id && b.vendor_name) {
        vendorMap.set(b.vendor_id, b.vendor_name);
      }
    });
    
    return Array.from(vendorMap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const toggleBillSelection = (billId, balance) => {
    setSelectedBills(prev => {
      const newSelected = { ...prev };
      if (newSelected[billId]) {
        delete newSelected[billId];
        setPaymentAmounts(pa => {
          const newAmounts = { ...pa };
          delete newAmounts[billId];
          return newAmounts;
        });
      } else {
        newSelected[billId] = true;
        setPaymentAmounts(pa => ({ ...pa, [billId]: balance }));
      }
      return newSelected;
    });
  };

  const updatePaymentAmount = (billId, amount, maxBalance) => {
    const numAmount = parseFloat(amount) || 0;
    setPaymentAmounts(prev => ({
      ...prev,
      [billId]: Math.min(numAmount, maxBalance)
    }));
  };

  const selectAll = () => {
    const bills = getCurrentBills();
    const newSelected = {};
    const newAmounts = {};
    bills.forEach(bill => {
      newSelected[bill.id] = true;
      newAmounts[bill.id] = bill.balance;
    });
    setSelectedBills(newSelected);
    setPaymentAmounts(newAmounts);
  };

  const clearSelection = () => {
    setSelectedBills({});
    setPaymentAmounts({});
  };

  const getTotalSelected = () => {
    return Object.entries(paymentAmounts)
      .filter(([id]) => selectedBills[id])
      .reduce((sum, [, amount]) => sum + (parseFloat(amount) || 0), 0);
  };

  const getSelectedCount = () => {
    return Object.keys(selectedBills).length;
  };

  const handlePayment = async () => {
    if (processing) return; // Prevent double-click
    
    if (getSelectedCount() === 0) {
      toast.error('Please select at least one bill');
      return;
    }

    if (paymentMethod === 'bank' && !bankAccount) {
      toast.error('Please select a bank account');
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      
      const billIds = Object.keys(selectedBills);
      const amounts = billIds.map(id => parseFloat(paymentAmounts[id]) || 0);
      const totalAmount = amounts.reduce((sum, a) => sum + a, 0);
      
      // Check if we're paying opening liabilities or regular bills
      if (activeTab === 'opening_liabilities') {
        // Pay opening liabilities - send as JSON body
        const response = await axios.post(`${API}/opening-liabilities/pay`, {
          liability_ids: billIds,
          amounts: amounts,
          payment_method: paymentMethod,
          bank_account: bankAccount || null,
          payment_reference: paymentReference || null,
          remarks: remarks || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // If bank payment, create bank debit transaction
        if (paymentMethod === 'bank' && bankAccount) {
          const currentLiabilities = getCurrentBills();
          const entityNames = currentLiabilities
            .filter(l => billIds.includes(l.id))
            .map(l => l.entity_name || l.vendor_name || l.employee_name || 'Unknown')
            .filter((v, i, a) => a.indexOf(v) === i)
            .join(', ');

          await axios.post(`${API}/bank-transactions`, {
            date: new Date().toISOString().split('T')[0],
            bank_account: bankAccount,
            transaction_type: 'debit',
            amount: totalAmount,
            reference: `[VENDOR PAYMENTS/${entityNames}] Opening liability payment - ${paymentReference || ''}`
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        toast.success(`Payment processed: ${response.data.results.length} liabilities paid`);
      } else {
        // Pay regular vendor bills
        const response = await axios.post(`${API}/purchase-bills/pay`, {
          bill_ids: billIds,
          amounts: amounts,
          payment_category: activeTab,
          payment_method: paymentMethod,
          payment_reference: paymentReference || undefined,
          bank_account: bankAccount || undefined,
          remarks: remarks || undefined
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // If bank payment, create bank debit transaction
        if (paymentMethod === 'bank' && bankAccount) {
          const currentBills = getCurrentBills();
          const vendorNames = currentBills
            .filter(b => billIds.includes(b.id))
            .map(b => b.vendor_name)
            .filter((v, i, a) => a.indexOf(v) === i)
            .join(', ');

          await axios.post(`${API}/bank-transactions`, {
            date: new Date().toISOString().split('T')[0],
            bank_account: bankAccount,
            transaction_type: 'debit',
            amount: totalAmount,
            reference: `[VENDOR PAYMENTS/${vendorNames}] ${paymentReference || 'Vendor bill payment'}`
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        toast.success(`Payment processed: ${response.data.results.length} bills paid`);
      }
      
      // Reset form
      setSelectedBills({});
      setPaymentAmounts({});
      setPaymentReference('');
      setRemarks('');
      
      // Refresh bills
      fetchBills();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Payment failed'));
    } finally {
      setProcessing(false);
    }
  };

  const getTabInfo = (view) => {
    switch(view) {
      case 'today':
        return { 
          icon: Calendar, 
          label: "Today's Bills", 
          description: "Bills dated today",
          color: 'text-blue-600',
          bgColor: 'bg-blue-50'
        };
      case 'current_month':
        return { 
          icon: Clock, 
          label: 'Current Month Pending', 
          description: "This month's carry-forward bills",
          color: 'text-orange-600',
          bgColor: 'bg-orange-50'
        };
      case 'old_month':
        return { 
          icon: History, 
          label: 'Old Month Liabilities', 
          description: "Previous months' unpaid bills (affects profit)",
          color: 'text-red-600',
          bgColor: 'bg-red-50'
        };
      case 'opening_liabilities':
        return { 
          icon: FileStack, 
          label: 'Opening Year Liabilities', 
          description: "Carry-forward liabilities from previous years",
          color: 'text-purple-600',
          bgColor: 'bg-purple-50'
        };
      default:
        return { icon: Wallet, label: '', description: '', color: '', bgColor: '' };
    }
  };

  if (!canPay) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">You don&apos;t have permission to make payments</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pay-bills-page">
      <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
        Pay Purchase Bills
      </h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['today', 'current_month', 'old_month', 'opening_liabilities'].map(tab => {
          const info = getTabInfo(tab);
          const bills = tab === 'today' ? todayBills : 
                        tab === 'current_month' ? currentMonthBills : 
                        tab === 'old_month' ? oldMonthBills :
                        openingLiabilities;
          const total = bills.reduce((sum, b) => sum + b.balance, 0);
          const Icon = info.icon;
          
          return (
            <Card 
              key={tab}
              className={`cursor-pointer transition-all ${activeTab === tab ? 'ring-2 ring-blue-500' : ''} ${info.bgColor}`}
              onClick={() => { setActiveTab(tab); setVendorFilter('all'); clearSelection(); }}
            >
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${info.color}`} />
                  <span className="font-medium">{info.label}</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(total)}</p>
                <p className="text-xs text-gray-500">{bills.length} bills</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bills List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">{getTabInfo(activeTab).label}</CardTitle>
                <div className="flex gap-2">
                  {/* Vendor Filter - Only show for regular bill tabs */}
                  {activeTab !== 'opening_liabilities' && getVendorsForCurrentTab().length > 0 && (
                    <Select 
                      value={vendorFilter} 
                      onValueChange={(value) => {
                        setVendorFilter(value);
                        clearSelection();
                      }}
                    >
                      <SelectTrigger className="w-[180px]" data-testid="vendor-filter">
                        <SelectValue placeholder="Filter by Vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Vendors</SelectItem>
                        {getVendorsForCurrentTab().map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                  <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                </div>
              </div>
              <p className="text-sm text-gray-500">{getTabInfo(activeTab).description}</p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : getCurrentBills().length === 0 ? (
                <div className="text-center py-8 text-gray-500">No bills to pay</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Vendor</TableHead>
                      {activeTab !== 'opening_liabilities' && <TableHead>Bill No.</TableHead>}
                      {activeTab !== 'opening_liabilities' && <TableHead>Date</TableHead>}
                      {activeTab === 'opening_liabilities' && <TableHead>Remarks</TableHead>}
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Pay Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getCurrentBills().map((bill) => (
                      <TableRow key={bill.id} className={selectedBills[bill.id] ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={!!selectedBills[bill.id]}
                            onCheckedChange={() => toggleBillSelection(bill.id, bill.balance)}
                            disabled={processing}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {bill.entity_name || bill.vendor_name || bill.employee_name || 'Unknown'}
                        </TableCell>
                        {activeTab !== 'opening_liabilities' && <TableCell>{bill.bill_number}</TableCell>}
                        {activeTab !== 'opening_liabilities' && <TableCell>{new Date(bill.bill_date).toLocaleDateString('en-IN')}</TableCell>}
                        {activeTab === 'opening_liabilities' && <TableCell className="text-sm text-gray-500">{bill.remarks || '-'}</TableCell>}
                        <TableCell className="text-right text-red-600 font-semibold">
                          {formatCurrency(bill.balance)}
                        </TableCell>
                        <TableCell className="text-right">
                          {selectedBills[bill.id] ? (
                            <Input
                              type="number"
                              step="0.01"
                              className="w-28 text-right"
                              value={paymentAmounts[bill.id] || ''}
                              onChange={(e) => updatePaymentAmount(bill.id, e.target.value, bill.balance)}
                              disabled={processing}
                            />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Form */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Selected Bills:</span>
                  <span className="font-bold">{getSelectedCount()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(getTotalSelected())}</span>
                </div>
              </div>

              {activeTab === 'old_month' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>Note:</strong> Paying old month bills uses current month&apos;s profit to clear past liabilities.
                  </p>
                </div>
              )}

              <div>
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Cash Payment Warning */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Note:</strong> This page is for bank transfers and cheque payments. Cash payments by cashiers are handled via Day Closing deductions.
                </p>
              </div>

              {paymentMethod === 'bank' && (
                <div>
                  <Label>Bank Account</Label>
                  <Select value={bankAccount} onValueChange={setBankAccount}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {BANK_ACCOUNTS.map(acc => (
                        <SelectItem key={acc} value={acc}>{acc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Reference / Cheque No.</Label>
                <Input 
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <div>
                <Label>Remarks</Label>
                <Textarea 
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional"
                  rows={2}
                />
              </div>

              <Button 
                className="w-full" 
                size="lg"
                onClick={handlePayment}
                disabled={getSelectedCount() === 0 || processing}
              >
                {processing ? 'Processing...' : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Pay {formatCurrency(getTotalSelected())}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PayBills;
